import React, { useEffect, useState } from "react";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import "./Vacancies.scss";

const PendingApprovals = () => {
  const { hasRole } = useAuth();
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  // Remark modal state
  const [modal, setModal] = useState(null); // { vacancyId, type, status }
  const [remark, setRemark] = useState("");

  const isHOD = hasRole("ROLE_HOD");
  const isHR = hasRole("ROLE_HR");
  const isAdmin = hasRole("ROLE_ADMIN");

  // Pick the right fetch URL based on role
  const fetchUrl = isHOD
    ? `${process.env.REACT_APP_API_HOD_URL}/recruitment/vacancies/all`
    : `${process.env.REACT_APP_API_HR_URL}/recruitment/vacancies/all`;

  useEffect(() => {
    axios
      .get(fetchUrl)
      .then((res) => {
        const all = res.data.data || [];
        // Show only non-fully-approved vacancies
        const pending = all.filter(
          (v) => v.vacancyStatus === "PENDING" || v.vacancyStatus === "OPEN",
        );
        setVacancies(pending);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [fetchUrl]);

  // Open remark modal before approving/rejecting
  const openModal = (vacancyId, type, status) => {
    setModal({ vacancyId, type, status });
    setRemark("");
  };

  const closeModal = () => {
    setModal(null);
    setRemark("");
  };

  const handleApproval = async () => {
    if (!modal) return;
    const { vacancyId, type, status } = modal;
    const key = `${vacancyId}-${type}-${status}`;
    setActionLoading(key);

    try {
      // Both HOD and HR now use the same payload: { status, remark }
      const payload = { status, remark };

      const url =
        type === "hod"
          ? `${process.env.REACT_APP_API_HOD_URL}/recruitment/vacancies/vacancy/${vacancyId}/approval`
          : `${process.env.REACT_APP_API_HR_URL}/recruitment/vacancies/vacancy/${vacancyId}/approval`;

      await axios.patch(url, payload);
      toast.success(`Vacancy ${status.toLowerCase()} successfully!`);
      setVacancies((prev) => prev.filter((v) => v.vacancyId !== vacancyId));
      closeModal();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Action failed. Please try again.",
      );
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading)
    return <div className="vacancy-loader">Loading pending approvals...</div>;

  const hodPending = vacancies.filter(
    (v) => (v.HODApprovalStatus || "").toUpperCase() !== "APPROVED",
  ).length;
  const hrPending = vacancies.filter(
    (v) => (v.HRApprovalStatus || "").toUpperCase() !== "APPROVED",
  ).length;

  return (
    <div className="vacancy-page">
      <div className="vacancy-page__header">
        <div className="vacancy-page__header-main">
          <div className="vacancy-page__title-wrap">
            <h2 className="vacancy-page__title">Pending Approvals</h2>
            <span className="vacancy-page__count">
              {vacancies.length} pending
            </span>
          </div>
          <p className="vacancy-page__sub">
            Review outstanding vacancy requests and move them forward quickly
          </p>
        </div>
      </div>

      <div className="vacancy-stats-row vacancy-stats-row--compact">
        <div className="vacancy-stat" style={{ "--accent": "#d97706" }}>
          <div className="vacancy-stat__icon">⏳</div>
          <div className="vacancy-stat__body">
            <p className="vacancy-stat__label">Open Requests</p>
            <p className="vacancy-stat__value">{vacancies.length}</p>
          </div>
          <div className="vacancy-stat__bar" />
        </div>
        <div className="vacancy-stat" style={{ "--accent": "#2563eb" }}>
          <div className="vacancy-stat__icon">👔</div>
          <div className="vacancy-stat__body">
            <p className="vacancy-stat__label">Awaiting HOD</p>
            <p className="vacancy-stat__value">{hodPending}</p>
          </div>
          <div className="vacancy-stat__bar" />
        </div>
        <div className="vacancy-stat" style={{ "--accent": "#6d28d9" }}>
          <div className="vacancy-stat__icon">🧾</div>
          <div className="vacancy-stat__body">
            <p className="vacancy-stat__label">Awaiting HR</p>
            <p className="vacancy-stat__value">{hrPending}</p>
          </div>
          <div className="vacancy-stat__bar" />
        </div>
      </div>

      {vacancies.length === 0 ? (
        <div className="vacancy-empty">No pending vacancy requests.</div>
      ) : (
        <div className="vacancy-cards">
          {vacancies.map((v) => (
            <div key={v.vacancyId} className="vacancy-card">
              <div className="vacancy-card__header">
                <div>
                  <h3>{v.jobTitle}</h3>
                  <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                    ID #{v.vacancyId}
                  </div>
                </div>
                <span className="badge badge--pending">Pending</span>
              </div>

              <div className="vacancy-card__meta">
                <span>🏢 {v.departmentName || `Dept ${v.departmentId}`}</span>
                <span>
                  👥 {v.noOfVacancy} position{v.noOfVacancy !== 1 ? "s" : ""}
                </span>
                <span>💼 {v.jobType}</span>
                <span>🧑‍💻 {v.employmentType}</span>
                {v.city && (
                  <span>
                    📍 {v.city}
                    {v.state ? `, ${v.state}` : ""}
                  </span>
                )}
              </div>

              {v.reasonOfVacancy && (
                <p className="vacancy-card__reason">
                  <strong>Reason:</strong> {v.reasonOfVacancy}
                </p>
              )}

              {v.hodRemark && (
                <p className="vacancy-card__remark">
                  <strong>HOD Remark:</strong> {v.hodRemark}
                </p>
              )}

              {v.hrRemark && (
                <p className="vacancy-card__remark">
                  <strong>HR Remark:</strong> {v.hrRemark}
                </p>
              )}

              <div className="vacancy-card__actions">
                {(isHOD || isAdmin) && (
                  <>
                    <button
                      className="btn btn--approve"
                      onClick={() => openModal(v.vacancyId, "hod", "APPROVED")}
                    >
                      ✓ HOD Approve
                    </button>
                    <button
                      className="btn btn--reject"
                      onClick={() => openModal(v.vacancyId, "hod", "REJECTED")}
                    >
                      ✗ HOD Reject
                    </button>
                  </>
                )}

                {(isHR || isAdmin) && (
                  <>
                    <button
                      className="btn btn--approve"
                      onClick={() => openModal(v.vacancyId, "hr", "APPROVED")}
                    >
                      ✓ HR Approve
                    </button>
                    <button
                      className="btn btn--reject"
                      onClick={() => openModal(v.vacancyId, "hr", "REJECTED")}
                    >
                      ✗ HR Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Remark Modal ── */}
      {modal && (
        <div className="va-modal-overlay" onClick={closeModal}>
          <div className="va-modal" onClick={(e) => e.stopPropagation()}>
            <div className="va-modal__header">
              <h3 className="va-modal__title">
                {modal.status === "APPROVED"
                  ? "✓ Confirm Approval"
                  : "✗ Confirm Rejection"}
              </h3>
              <button className="va-modal__close" onClick={closeModal}>
                ×
              </button>
            </div>

            <p className="va-modal__desc">
              Add an optional remark before{" "}
              {modal.status === "APPROVED" ? "approving" : "rejecting"} this
              vacancy.
            </p>

            <textarea
              className="va-modal__textarea"
              rows={3}
              placeholder="Remark (optional)..."
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />

            <div className="va-modal__actions">
              <button className="cv-btn cv-btn--ghost" onClick={closeModal}>
                Cancel
              </button>
              <button
                className={`cv-btn ${modal.status === "APPROVED" ? "cv-btn--approve" : "cv-btn--reject"}`}
                onClick={handleApproval}
                disabled={!!actionLoading}
              >
                {actionLoading
                  ? "Processing..."
                  : modal.status === "APPROVED"
                    ? "Confirm Approval"
                    : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingApprovals;
